import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import SimpleBar from "simplebar-react";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Spinner, UncontrolledDropdown } from "reactstrap";
import { Icon, UserAvatar } from "../../../components/Component";
import getTextColor from "../../../components/custom/getTextColor";
import { buildTimeline, dayLabel } from "./livechatState";
import { visitorName } from "./LivechatAside";
import LivechatComposer from "./LivechatComposer";
import { hasMedia, LivechatAttachment, LivechatImageViewer, LivechatUploading } from "./LivechatMedia";
import LivechatModalAssign from "./LivechatModalAssign";
import LivechatModalResolve from "./LivechatModalResolve";
import LivechatModalTag from "./LivechatModalTag";

const LOAD_OLDER_OFFSET_PX = 60;
// writer of the backend's notices in Live Chat's internal notes
const SYSTEM_AGENT_ID = "crm-system";
const STICK_TO_BOTTOM_PX = 120;

const SystemPill = ({ children }) => (
  <div className="d-flex justify-content-center py-3 overflow-hidden">
    <span className="lc-system">{children}</span>
  </div>
);

const MessageStatus = ({ message, onResend }) => {
  if (message.local === "sending") return <span className="lc-time">Mengirim...</span>;

  if (message.local === "uploading") {
    const { stage, percent } = message.progress ?? {};
    const label = stage === "upload" ? "Mengunggah" : "Mengompres";

    return <span className="lc-time">{percent === undefined ? `${label}...` : `${label} ${percent}%`}</span>;
  }

  if (message.local === "uploadFailed") return <span className="lc-failed">{message.error}</span>;

  if (message.local === "rejected") return <span className="lc-failed">Tidak terkirim, tiket sudah ditutup</span>;

  if (message.local === "failed") {
    return (
      <button type="button" className="btn btn-sm btn-link lc-failed p-0" onClick={() => onResend(message.client_id)}>
        <Icon name="alert-circle" className="mr-1" />
        Gagal, kirim ulang
      </button>
    );
  }

  const time = <span className="lc-time">{moment(message.created_at).format("HH:mm")}</span>;

  if (message.sender?.type !== "agent") return time;

  // ✓ sent, blue ✓✓ once the visitor read it, as on the WhatsApp chat
  return (
    <>
      <i
        className={`bi ${message.read_at ? "bi-check2-all text-info" : "bi-check2"} lc-tick`}
        aria-label={message.read_at ? "Dibaca" : "Terkirim"}
      />{" "}
      {time}
    </>
  );
};

const MessageBubble = ({ conversationId, message, myId, onReply, onResend, onOpenImage, onUnsend }) => {
  const isAgent = message.sender?.type === "agent";
  const isSettled = !message.local && !message.deleted_at;
  const canReply = Boolean(onReply) && isSettled;
  // only the agent's own messages, never the visitor's or another sales'
  const canUnsend = Boolean(onUnsend) && isSettled && isAgent && message.sender.id === myId;

  return (
    <div className={`chat ${isAgent ? "is-me" : "is-you"}`}>
      <div className="chat-content">
        <div className="chat-bubbles">
          <div className="chat-bubble">
            <div className="chat-msg lc-msg">
              {message.deleted_at ? (
                <span className="lc-unsent">Pesan ditarik</span>
              ) : (
                <>
                  {message.reply_to && (
                    <div className="lc-reply">
                      {message.reply_to.deleted
                        ? "Pesan ditarik"
                        : message.reply_to.text || `[${message.reply_to.type}]`}
                    </div>
                  )}
                  {message.local && message.attachment && <LivechatUploading message={message} />}
                  {hasMedia(message) && (
                    <LivechatAttachment conversationId={conversationId} message={message} onOpenImage={onOpenImage} />
                  )}
                  {message.text && <div className={hasMedia(message) ? "mt-1" : undefined}>{message.text}</div>}
                </>
              )}
              <div className="text-right">
                <MessageStatus message={message} onResend={onResend} />
              </div>
            </div>
            {(canReply || canUnsend) && (
              <ul className="chat-msg-more">
                <li className="d-none d-sm-block">
                  <UncontrolledDropdown>
                    <DropdownToggle
                      tag="button"
                      type="button"
                      className="btn btn-icon btn-sm btn-trigger"
                      aria-label="Opsi pesan"
                    >
                      <Icon name="more-v" />
                    </DropdownToggle>
                    <DropdownMenu right>
                      <ul className="link-list-opt no-bdr">
                        {canReply && (
                          <li>
                            <DropdownItem tag="button" type="button" onClick={() => onReply(message)}>
                              <span>Balas</span>
                            </DropdownItem>
                          </li>
                        )}
                        {canUnsend && (
                          <li>
                            <DropdownItem tag="button" type="button" onClick={() => onUnsend(message)}>
                              <span className="text-danger">Tarik pesan</span>
                            </DropdownItem>
                          </li>
                        )}
                      </ul>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TimelineEntry = ({ conversationId, entry, myId, onReply, onResend, onOpenImage, onUnsend }) => {
  if (entry.kind === "date") {
    return <div className="time-separator">{dayLabel(entry.at)}</div>;
  }

  if (entry.kind === "separator") {
    return <SystemPill>Tiket baru dibuka, {moment(entry.at).format("DD/MM/YYYY, HH.mm")}</SystemPill>;
  }

  // the CRM's own notices (auto-revert) read as system lines, as on WhatsApp
  if (entry.kind === "note" && entry.item.agent_id === SYSTEM_AGENT_ID) {
    return <SystemPill>{entry.item.text}</SystemPill>;
  }

  if (entry.kind === "note") {
    return (
      <div className="lc-note">
        <strong>Catatan internal</strong>, {entry.item.agent_id}, {moment(entry.at).format("HH:mm")}
        <div>{entry.item.text}</div>
      </div>
    );
  }

  if (entry.item.sender?.type === "system") return <SystemPill>{entry.item.text}</SystemPill>;

  return (
    <MessageBubble
      conversationId={conversationId}
      message={entry.item}
      myId={myId}
      onUnsend={onUnsend}
      onReply={onReply}
      onResend={onResend}
      onOpenImage={onOpenImage}
    />
  );
};

const LivechatBody = ({
  conversation,
  messages,
  notes,
  ticket,
  hasOlder,
  options,
  profile,
  composer,
  ticketActions,
  onLoadOlder,
  onAssigned,
  onBack,
}) => {
  const scrollRef = useRef(null);
  const anchorRef = useRef(null);
  const lastConversationRef = useRef(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [viewedImage, setViewedImage] = useState(null);
  const [replyTo, setReplyTo] = useState(null);

  useEffect(() => setReplyTo(null), [conversation?.conversationId]);
  const timeline = useMemo(() => buildTimeline(messages, notes), [messages, notes]);
  const department = options.departments[conversation?.departmentId];
  const site = options.channels[conversation?.channelId];

  // keeps the view still when older messages are prepended, and follows new ones at the bottom
  useLayoutEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    if (anchorRef.current && messages[0]?.id !== anchorRef.current.firstId) {
      el.scrollTop = el.scrollHeight - anchorRef.current.height + anchorRef.current.top;
      anchorRef.current = null;
    } else if (anchorRef.current) {
      return;
    } else if (lastConversationRef.current !== conversation?.conversationId) {
      el.scrollTop = el.scrollHeight;
    } else if (el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX) {
      el.scrollTop = el.scrollHeight;
    }

    if (messages.length) lastConversationRef.current = conversation?.conversationId;
  }, [timeline, conversation?.conversationId, messages]);

  const loadOlder = async () => {
    const el = scrollRef.current;

    if (!el || !hasOlder || isLoadingOlder) return;

    anchorRef.current = { height: el.scrollHeight, top: el.scrollTop, firstId: messages[0]?.id };
    setIsLoadingOlder(true);

    try {
      await onLoadOlder();
    } finally {
      // nothing was prepended (error, no more pages): don't leave the anchor for a later render
      anchorRef.current = null;
      setIsLoadingOlder(false);
    }
  };

  const onScroll = (e) => {
    if (e.currentTarget.scrollTop <= LOAD_OLDER_OFFSET_PX) loadOlder();
  };

  if (!conversation) return null;

  return (
    <div className={`nk-chat-body position-relative show-chat ${profile.isOpen ? "profile-shown" : ""}`}>
      <div className="nk-chat-head">
        <ul className="nk-chat-head-info">
          <li className="nk-chat-body-close">
            <button
              type="button"
              className="btn btn-icon btn-trigger nk-chat-hide ml-n1"
              aria-label="Kembali"
              onClick={onBack}
            >
              <Icon name="arrow-left" />
            </button>
          </li>
          <li className="nk-chat-head-user">
            <div className="user-card">
              <UserAvatar icon="user-alt-fill" className="overflow-hidden" />
              <div className="user-info">
                <div className="lead-text">{visitorName(conversation)}</div>
                {department && (
                  <span
                    className="badge rounded-pill"
                    style={{ background: department.color, color: getTextColor(department.color), margin: 2 }}
                  >
                    {department.name}
                  </span>
                )}
                {site && (
                  <span className="badge rounded-pill lc-holder" style={{ margin: 2 }}>
                    {site.name}
                  </span>
                )}
                {ticket?.tags?.map((tag) => (
                  <span
                    key={tag.id}
                    className="badge rounded-pill"
                    style={{ background: tag.color, color: getTextColor(tag.color), margin: 2 }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          </li>
        </ul>
        <ul className="nk-chat-head-tools info-chat-head">
          <li>
            <button
              type="button"
              className="btn btn-icon btn-trigger text-primary"
              aria-label="Assign"
              title="Assign"
              onClick={() => setIsAssignOpen(true)}
            >
              <Icon name="emails" />
            </button>
          </li>
          <li>
            <button
              type="button"
              className="btn btn-icon btn-trigger text-primary"
              aria-label="Tag"
              title="Tag"
              onClick={() => setIsTagOpen(true)}
            >
              <Icon name="hash" />
            </button>
          </li>
          <li>
            <button
              type="button"
              className="btn btn-icon btn-trigger text-primary"
              aria-label={profile.isOpen ? "Tutup profil" : "Buka profil"}
              aria-pressed={profile.isOpen}
              onClick={profile.onToggle}
            >
              <Icon name="user-circle" />
            </button>
          </li>
          {ticketActions.isResolved ? null : ticketActions.isEnded ? (
            <li className="mr-n1 mr-md-n2">
              <Button
                color="primary"
                className="btn-round"
                size="xs"
                disabled={ticketActions.isBusy}
                onClick={() => ticketActions.onChange("OPENED")}
              >
                Buka kembali
              </Button>
            </li>
          ) : (
            <>
              <li className="mr-n1 mr-md-n1">
                <Button
                  color="secondary"
                  className="btn-round"
                  size="xs"
                  title={ticketActions.canSolve ? undefined : "Hanya Sales atau CS"}
                  disabled={ticketActions.isBusy || !ticketActions.canSolve}
                  onClick={() => ticketActions.onChange("SOLVED")}
                >
                  Mark as SOLVED
                </Button>
              </li>
              <li className="mr-n1 mr-md-n2">
                <Button
                  color="primary"
                  className="btn-round"
                  size="xs"
                  title={ticketActions.canResolve ? undefined : "Hanya Sales atau CS pemegang chat"}
                  disabled={ticketActions.isBusy || !ticketActions.canResolve}
                  onClick={() => setIsResolveOpen(true)}
                >
                  Resolve
                </Button>
              </li>
            </>
          )}
        </ul>
      </div>

      <SimpleBar
        className="nk-chat-panel"
        style={{ overflowAnchor: "none" }}
        scrollableNodeProps={{ ref: scrollRef, onScroll }}
      >
        <div className="d-flex justify-content-center lc-older">
          {isLoadingOlder ? (
            <Spinner size="sm" color="primary" />
          ) : hasOlder ? (
            <Button outline color="light" size="sm" onClick={loadOlder}>
              <span>Muat pesan sebelumnya</span>
              <Icon name="reload-alt" />
            </Button>
          ) : (
            messages.length > 0 && (
              <Button outline color="danger" size="sm" disabled>
                <span>Awal percakapan</span>
                <Icon name="na" />
              </Button>
            )
          )}
        </div>
        {timeline.map((entry) => (
          <TimelineEntry
            conversationId={conversation.conversationId}
            myId={composer.myId}
            onUnsend={composer.unsend}
            onOpenImage={setViewedImage}
            key={`${entry.kind}-${entry.id}`}
            entry={entry}
            onReply={composer.canReply ? setReplyTo : undefined}
            onResend={composer.resend}
          />
        ))}
      </SimpleBar>

      {composer.isVisitorTyping && <div className="lc-typing">{visitorName(conversation)} sedang mengetik…</div>}

      {composer.canReply && (
        <LivechatComposer
          departmentId={conversation.departmentId}
          isDisabled={composer.isDisabled}
          onReopen={
            ticketActions.isEnded && !ticketActions.isResolved ? () => ticketActions.onChange("OPENED") : undefined
          }
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onTyping={composer.onTyping}
          accept={composer.accept}
          onSendFile={composer.sendFile}
          onSend={(text, quoted) => {
            composer.send(text, quoted);
            setReplyTo(null);
          }}
        />
      )}

      <LivechatModalTag
        isOpen={isTagOpen}
        conversationId={conversation.conversationId}
        departmentId={conversation.departmentId}
        isResolved={ticketActions.isResolved}
        tags={ticketActions.tags}
        currentTagIds={ticketActions.currentTagIds}
        onClose={() => setIsTagOpen(false)}
        onSaved={(tags) => {
          setIsTagOpen(false);
          ticketActions.onTagsSaved(tags);
        }}
      />

      <LivechatImageViewer image={viewedImage} onClose={() => setViewedImage(null)} />

      <LivechatModalResolve
        isOpen={isResolveOpen}
        conversation={conversation}
        contact={ticketActions.contact}
        ticket={ticket}
        onClose={() => setIsResolveOpen(false)}
        onResolved={(updated) => {
          setIsResolveOpen(false);
          ticketActions.onResolved(updated);
        }}
      />

      <LivechatModalAssign
        isOpen={isAssignOpen}
        conversation={conversation}
        onClose={() => setIsAssignOpen(false)}
        onAssigned={(updated) => {
          setIsAssignOpen(false);
          onAssigned(updated);
        }}
      />
    </div>
  );
};

export default LivechatBody;
